import { useMemo, useState } from "react";
import Card from "@rio-cloud/rio-uikit/Card";
import TableHead from "@rio-cloud/rio-uikit/TableHead";
import type { SortDirectionType } from "@rio-cloud/rio-uikit/SortDirection";
import { useT } from "@hooks";
import type { MaintenanceVehicleCost } from "@services/maintenance";

interface TopVehiclesMaintenanceTableProps {
  items: MaintenanceVehicleCost[];
}

type SortKey = keyof MaintenanceVehicleCost;

const BAR_COLOR = "#3DD9C0";
const BAR_HEIGHT = 20;
const BAR_RADIUS = 4;
const HEADER_LABEL_STYLE = { fontWeight: 500, fontSize: 13 } as const;
const CELL_STYLE = { fontSize: 13, verticalAlign: "middle" } as const;

const formatCurrency = (value: number) =>
  `R$ ${value.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const compareItems = (a: MaintenanceVehicleCost, b: MaintenanceVehicleCost, key: SortKey) => {
  if (key === "vehicle") return a.vehicle.localeCompare(b.vehicle);
  return a[key] - b[key];
};

export default function TopVehiclesMaintenanceTable({ items }: Readonly<TopVehiclesMaintenanceTableProps>) {
  const t = useT();
  const [sortKey, setSortKey] = useState<SortKey | null>("amount");
  const [sortDirection, setSortDirection] = useState<SortDirectionType>("desc");

  const COLUMNS: { key: SortKey; label: string; className?: string }[] = [
    { key: "vehicle", label: t("maintenance.chart.topVehicles.vehicle") },
    { key: "amount", label: t("maintenance.chart.topVehicles.amount") },
    { key: "costPerKm", label: t("maintenance.chart.topVehicles.costPerKm"), className: "text-right" },
  ];

  const maxAmount = items.reduce((max, item) => Math.max(max, item.amount), 0);

  const sortedItems = useMemo(() => {
    if (!sortKey) return items;
    const factor = sortDirection === "asc" ? 1 : -1;
    return [...items].sort((a, b) => factor * compareItems(a, b, sortKey));
  }, [items, sortKey, sortDirection]);

  const handleSortChange = (event: React.MouseEvent<HTMLTableCellElement>) => {
    const column = event.currentTarget.dataset.sortby as SortKey | undefined;
    if (!column) return;

    if (column === sortKey) {
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else {
        setSortKey(null);
      }
      return;
    }

    setSortKey(column);
    setSortDirection("asc");
  };

  return (
    <Card className="padding-y-25 padding-x-15 padding-x-25-sm display-flex flex-column height-100pct">
      <div className="padding-bottom-20">
        <span className="text-size-16 text-bold">{t("maintenance.chart.topVehicles")}</span>
      </div>
      <div className="table-responsive rounded border border-width-1 border-color-light">
        <table className="table table-hover margin-bottom-0">
          <thead>
            <tr className="bg-lightest">
              {COLUMNS.map((column) => (
                <TableHead
                  key={column.key}
                  column={column.key}
                  label={<span style={HEADER_LABEL_STYLE}>{column.label}</span>}
                  title={column.label}
                  sortBy={sortKey ?? ""}
                  sortDir={sortKey ? sortDirection : undefined}
                  onClick={handleSortChange}
                  className={`text-color-gray cursor-pointer white-space-nowrap ${column.className ?? ""}`.trim()}
                />
              ))}
            </tr>
          </thead>
          <tbody data-testid="corpo-veiculos">
            {sortedItems.length === 0 ? (
              <tr>
                <td colSpan={3} className="text-center text-color-gray padding-20">
                  {t("common.label.noData")}
                </td>
              </tr>
            ) : (
              sortedItems.map((item) => {
                const percentage = maxAmount > 0 ? (item.amount / maxAmount) * 100 : 0;
                return (
                  <tr key={item.vehicle}>
                    <td style={{ ...CELL_STYLE, whiteSpace: "nowrap" }}>{item.vehicle}</td>
                    <td style={{ ...CELL_STYLE, width: "55%" }}>
                      <div className="display-flex align-items-center gap-10 min-width-0">
                        <div
                          data-testid="barra-veiculo"
                          style={{
                            width: `${percentage}%`,
                            height: BAR_HEIGHT,
                            borderRadius: BAR_RADIUS,
                            backgroundColor: BAR_COLOR,
                            flexShrink: 1,
                            minWidth: 0,
                          }}
                        />
                        <span className="white-space-nowrap">{formatCurrency(item.amount)}</span>
                      </div>
                    </td>
                    <td className="text-right" style={{ ...CELL_STYLE, whiteSpace: "nowrap" }}>
                      {formatCurrency(item.costPerKm)}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
